import React, { useState, useEffect } from "react";
import Modal from "@modals/Modal";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "@store/slices/modalSlice";
import { setAssignToAllAdmins, setSelectedAssignedFileId, resetShouldRefreshFileInfo, setFileStatus, setTargetLanguageId, setValidationFileStatus } from "@store/slices/gaAdminSlice";
import { setFileMeta } from "@store/slices/fileMetaSlice";
import { useNavigate } from "react-router-dom";
import { BASE_URL, getStatusStyleGA } from "@utils/constants";
import { toast } from "react-toastify";
import apiClient from "@/services/api/apiClient";

const getStatusStyle = (status) => {
    const styles = {
        PENDING: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Pending" },
        GS_ASSIGNED: { bg: "bg-blue-100", text: "text-blue-800", label: "GS Admin Assigned" },
        DEV_ASSIGNED: { bg: "bg-purple-100", text: "text-purple-800", label: "Dev Admin Assigned" },
        TA_ASSIGNED: { bg: "bg-indigo-100", text: "text-indigo-800", label: "Translator Admin Assigned" },
        VA_ASSIGNED: { bg: "bg-pink-100", text: "text-pink-800", label: "Validator Admin Assigned" },
        DU_ASSIGNED: { bg: "bg-cyan-100", text: "text-cyan-800", label: "Dev User Assigned" },
        UNDER_TRANSLATION: { bg: "bg-orange-100", text: "text-orange-800", label: "Under Translation" },
        UNDER_VALIDATION: { bg: "bg-lime-100", text: "text-lime-800", label: "Validator User" },
        DEV_VALIDATED: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Dev Validated" },
        TRANSLATED: { bg: "bg-teal-100", text: "text-teal-800", label: "Translated" },
        VALIDATED: { bg: "bg-sky-100", text: "text-sky-800", label: "Validated" },
        GS_REJECTED: { bg: "bg-rose-100", text: "text-rose-800", label: "GS Admin Rejected" },
        DEV_REJECTED: { bg: "bg-red-100", text: "text-red-800", label: "Dev Admin Rejected" },
        COMPLETED: { bg: "bg-green-100", text: "text-green-800", label: "Completed" },
    };
    return styles[status] || { bg: "bg-gray-100", text: "text-gray-800", label: status };
};

const ViewAllAdminsInfo = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isOpen = useSelector((store) => store.modal.viewTranslatorAdmin);
    const { selectedAssignedFileId, assignedToAllAdmins, shouldRefreshFileInfo } = useSelector((store) => store.gaAdmin);
    const selectedFileData = useSelector((store) => store.gaAdmin.selectedFile);
    const fileId = useSelector((store) => store.fileMeta.fileId);
    const [fileInfo, setFileInfo] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sendingStates, setSendingStates] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(3);
    const { token } = useSelector((store) => store.auth);

    const handleClose = () => {
        dispatch(closeModal("viewTranslatorAdmin"));
    };

    useEffect(() => {
        if (fileId) {
            fetchFileInfo();
        }
    }, [fileId]);

    useEffect(() => {
        if (shouldRefreshFileInfo) {
            fetchFileInfo();
            dispatch(resetShouldRefreshFileInfo());
        }
    }, [shouldRefreshFileInfo]);

    const fetchFileInfo = async () => {
        try {
            setLoading(true);
            const data = await apiClient(
                BASE_URL + `api/gsAdmin/filesByfile?fileId=${fileId}`,
                {
                    method: 'GET',
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                },
                navigate
            );
            setFileInfo(data.data || []);
        } catch (error) {
            console.error(error);
            setFileInfo([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenFile = async (row) => {
        try {
            const fileId = row.fileId;
            dispatch(setFileMeta({ fileId: fileId, fileName: row.fileName }));
            dispatch(setSelectedAssignedFileId(fileId));
            dispatch(setFileStatus(row.translationStatus));
            dispatch(setValidationFileStatus(row.validationStatus));
            dispatch(setTargetLanguageId(row.targetLanguageId));
            navigate(`/GACSAdmin/folders/files/sentences`);
        } catch (error) {
            console.error("Error handling file click:", error);
        }
    };
    const handleSend = async (item) => {
        setSendingStates((prev) => ({ ...prev, [item.targetLanguageId]: true }));
        try {
            const data = await apiClient(BASE_URL + "api/gsAdmin/sendToCdac", {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    fileId: item.fileId,
                    sourceLanguageId: 2,
                    targetLanguageId: item.targetLanguageId,
                }),
            },navigate);
            if (data.status === "success") {
                toast.success(
                    <div className="p-3 font-serif text-sm font-medium text-[#d85d1a]">
                        Sent to C-DAC successfully!
                    </div>,
                    {
                        position: "top-right",
                        autoClose: 3000,
                        theme: "colored",
                        className: "rounded-lg shadow-md border border-green-200",
                    }
                );
                fetchFileInfo(); // Refresh file info to update status
            } else {
                toast.error(
                    <div className="p-3 font-serif text-sm text-white font-medium text-[#d85d1a]">
                        {data.message || "Failed to send to C-DAC."}
                    </div>,
                    {
                        position: "top-right",
                        autoClose: 3000,
                        theme: "colored",
                        className: "rounded-lg shadow-md border border-red-200",
                    }
                );
            }
        } catch (error) {
            toast.error(
                <div className="p-3 font-serif text-sm text-white font-medium text-[#d85d1a]">
                    Error sending to C-DAC.
                </div>,
                {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                    className: "rounded-lg shadow-md border border-red-200",
                }
            );
            console.error(error);
        } finally {
            setSendingStates((prev) => ({ ...prev, [item.targetLanguageId]: false }));
        }
    };
    const filteredFileInfo = Array.isArray(fileInfo)
        ? fileInfo.filter(item => item.targetLanguageName.toLowerCase() !== 'english')
        : [];

    const groupedByUsername = {};
    filteredFileInfo.forEach((item, index) => {
        if (!groupedByUsername[item.salutationFullName]) {
            groupedByUsername[item.salutationFullName] = [];
        }
        groupedByUsername[item.salutationFullName].push({ ...item, originalIndex: index });
    });

    const usernames = Object.keys(groupedByUsername);

    const indexOfLastUser = currentPage * itemsPerPage;
    const indexOfFirstUser = indexOfLastUser - itemsPerPage;
    const currentUsernames = usernames.slice(indexOfFirstUser, indexOfLastUser);
    const totalPages = Math.ceil(usernames.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pageNumbers = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        const totalEntries = filteredFileInfo.length;

        return (
            <div className="flex items-center justify-between mt-6 px-6">
                <div className="text-sm text-gray-600 bg-blue-50 px-3 py-1.5 rounded-lg font-['Semantic/Strong']">
                    Showing {indexOfFirstUser + 1} to {Math.min(indexOfLastUser, usernames.length)} of {usernames.length} users ({totalEntries} entries)
                </div>
                <div className="flex items-center space-x-1">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        className={`px-4 py-1.5 rounded-full text-sm font-['Semantic/Strong'] transition-all duration-200 ${currentPage === 1
                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed border border-gray-200'
                            : 'bg-white text-[#0066cc] border border-[#0066cc] hover:bg-[#0066cc] hover:text-white hover:scale-105 focus:ring-2 focus:ring-[#0066cc]/50'
                            }`}
                    >
                        Previous
                    </button>
                    {pageNumbers.map(number => (
                        <button
                            key={number}
                            onClick={() => handlePageChange(number)}
                            className={`px-3 py-1.5 rounded-full text-sm font-['Semantic/Strong'] transition-all duration-200 ${currentPage === number
                                ? 'bg-[#0066cc] text-white shadow-sm'
                                : 'bg-white text-[#0066cc] border border-[#0066cc] hover:bg-[#0066cc] hover:text-white hover:scale-105 focus:ring-2 focus:ring-[#0066cc]/50'
                                }`}
                        >
                            {number}
                        </button>
                    ))}
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        className={`px-4 py-1.5 rounded-full text-sm font-['Semantic/Strong'] transition-all duration-200 ${currentPage === totalPages
                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed border border-gray-200'
                            : 'bg-white text-[#0066cc] border border-[#0066cc] hover:bg-[#0066cc] hover:text-white hover:scale-105 focus:ring-2 focus:ring-[#0066cc]/50'
                            }`}
                    >
                        Next
                    </button>
                </div>
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Assigned Files"
            maxWidth="7xl"
            className="bg-gray-50 rounded-xl shadow-lg animate-fadeIn"
        >
            <div className="flex flex-col h-full max-h-[80vh] p-6 sm:p-8">
                <div className="flex-1 overflow-y-auto">
                    {/* <div className="sticky top-0 bg-white border-b-2 border-[#0066cc] py-4 mb-4">
                        <div className="flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-gray-800 font-['Semantic/Strong']">
                                List of Translator Admin(s)
                            </h2>
                            {filteredFileInfo.length > 0 && (
                                <div className="text-sm text-gray-600 bg-blue-50 px-3 py-1.5 rounded-lg font-['Semantic/Strong'] shadow-sm">
                                    Total: {filteredFileInfo.length} entries
                                </div>
                            )}
                        </div>
                    </div> */}

                    <div className="overflow-x-auto">
                        <table className="min-w-full border border-gray-200 text-sm text-gray-800 rounded-lg shadow-sm">
                            <thead className="bg-gray-100 text-gray-700 font-['Semantic/Strong']">
                                <tr>
                                    <th className="border border-gray-200 px-6 py-3 text-center">Sr. No.</th>
                                    <th className="border border-gray-200 px-6 py-3 text-center">User Name</th>
                                    <th className="border border-gray-200 px-6 py-3 text-center">Source Language</th>
                                    <th className="border border-gray-200 px-6 py-3 text-center">Target Language</th>
                                    <th className="border border-gray-200 px-6 py-3 text-center">Translation Status</th>
                                    <th className="border border-gray-200 px-6 py-3 text-center">Validation Status</th>
                                    <th className="border border-gray-200 px-6 py-3 text-center">Total Sentences</th>
                                    <th className="border border-gray-200 px-6 py-3 text-center">Translated Count</th>
                                    <th className="border border-gray-200 px-6 py-3 text-center">Rejected Count</th>
                                    <th className="border border-gray-200 px-6 py-3 text-center">Validated Count</th>
                                    <th className="border border-gray-200 px-6 py-3 text-center">Open File</th>
                                    <th className="border border-gray-200 px-6 py-3 text-center">Send to C-DAC</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="11" className="text-center py-8">
                                            <div className="flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-600 mr-2"></div>
                                                <span className="text-gray-600 font-['Semantic/Strong']">Loading admins...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredFileInfo.length === 0 ? (
                                    <tr>
                                        <td colSpan="11" className="bg-yellow-50 border border-yellow-400 text-yellow-800 px-6 py-6 text-center rounded-lg">
                                            <p className="font-['Semantic/Strong'] text-base">No files found.</p>
                                            <p className="mt-2 text-sm font-['Semantic/Strong']">
                                                You have not been assigned any admins. Please contact the administrator if this seems incorrect.
                                            </p>
                                        </td>
                                    </tr>
                                ) : (
                                    (() => {
                                        let srNo = indexOfFirstUser + 1;
                                        const rows = [];

                                        currentUsernames.forEach(username => {
                                            const userItems = groupedByUsername[username];
                                            const rowspan = userItems.length;

                                            userItems.forEach((item, userItemIndex) => {
                                                const isFirstRow = userItemIndex === 0;
                                                const style = getStatusStyle(item.validationStatus || '-');
                                                rows.push(
                                                    <tr
                                                        key={`${item.assignedTo}-${item.targetLanguageName}-${userItemIndex}`}
                                                        className={`${userItemIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                                            } hover:bg-gray-100 transition-colors duration-200`}
                                                    >
                                                        {isFirstRow && (
                                                            <td className="border border-gray-200 px-6 py-3 text-center font-['Semantic/Strong']" rowSpan={rowspan}>
                                                                {srNo}
                                                            </td>
                                                        )}
                                                        {isFirstRow && (
                                                            <td className="border border-gray-200 px-6 py-3 text-center font-['Semantic/Strong']" rowSpan={rowspan}>
                                                                {item.salutationFullName}
                                                            </td>
                                                        )}
                                                        <td className="border border-gray-200 px-6 py-3 text-center font-['Semantic/Strong']">English</td>
                                                        <td className="border border-gray-200 px-6 py-3 text-center font-['Semantic/Strong']">{item.targetLanguageName}</td>
                                                        <td className="border border-gray-200 px-6 py-3 text-center">
                                                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-['Semantic/Strong']  shadow-sm font-medium ${ getStatusStyleGA(item.translationStatus).bg} ${ getStatusStyleGA(item.translationStatus).text}`}>
                                                                {getStatusStyleGA(item.translationStatus).label}
                                                            </span>
                                                        </td>
                                                        <td className="border border-gray-200 px-6 py-3 text-center">
                                                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-['Semantic/Strong'] ${style.bg} ${style.text} shadow-sm`}>
                                                                {style.label}
                                                            </span>
                                                        </td>
                                                        <td className="border border-gray-200 px-6 py-3 text-center font-['Semantic/Strong']">{item.totalSentences}</td>
                                                        <td className="border border-gray-200 px-6 py-3 text-center">
                                                            <div className="flex flex-col items-center">
                                                                <span className="font-['Semantic/Strong']">{item.translatedCount}</span>
                                                                <span className="text-xs text-gray-500 font-['Semantic/Strong']">({item.percentCompleted}%)</span>
                                                            </div>
                                                        </td>
                                                        <td className="border border-gray-200 px-6 py-3 text-center font-['Semantic/Strong']">{item.rejectedCount}</td>
                                                        <td className="border border-gray-200 px-6 py-3 text-center font-['Semantic/Strong']">{item.validatedCount}</td>
                                                        <td className="border border-gray-200 px-6 py-3 text-center">
                                                            <button
                                                                className="bg-[#0066cc] text-white px-4 py-2 rounded-md hover:bg-[#0055b3] hover:scale-105 transition-all duration-200 font-['Semantic/Strong'] text-sm shadow-sm focus:ring-2 focus:ring-[#0066cc]/50"
                                                                onClick={() => handleOpenFile(item)}
                                                                aria-label={`Open file for ${item.salutationFullName} in ${item.targetLanguageName}`}
                                                            >
                                                                Open
                                                            </button>
                                                        </td>
                                                        <td className="border border-gray-200 px-6 py-3 text-center">
                                                            <button
                                                                className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-['Semantic/Strong'] transition-all duration-200 shadow-sm focus:ring-2 ${sendingStates[item.targetLanguageId]
                                                                    ? 'bg-blue-100 text-blue-500 cursor-not-allowed border border-blue-200'
                                                                    : (item.validationStatus === 'SUBMITTED')
                                                                        ? 'bg-green-600 text-white hover:bg-green-700 hover:scale-105 focus:ring-green-600/50'
                                                                        : 'bg-blue-100 text-blue-600 cursor-not-allowed border border-blue-200'
                                                                    }`}
                                                                onClick={() => handleSend(item)}
                                                                disabled={
                                                                    sendingStates[item.targetLanguageId] ||
                                                                    ['PENDING','COMPLETED', 'SUBMITTED_TO_CDAC'].includes(item.validationStatus)
                                                                }

                                                            >
                                                                {sendingStates[item.targetLanguageId] ? (
                                                                    <div className="flex items-center">
                                                                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-blue-500 mr-2"></div>
                                                                        Sending...
                                                                    </div>
                                                                ) : item.validationStatus === 'SUBMITTED' ? (
                                                                    'Send'
                                                                ) :  item.validationStatus === 'COMPLETED' || item.validationStatus === 'SUBMITTED_TO_CDAC' ? (
                                                                    'Sent'
                                                                ) : (
                                                                    'Cannot Send'
                                                                )}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                            srNo++;
                                        });

                                        return rows;
                                    })()
                                )}
                            </tbody>
                        </table>
                    </div>

                    {renderPagination()}
                </div>
            </div>
        </Modal>
    );
};

export default ViewAllAdminsInfo;